/**
 * Constantes de jeu : source de verite partagee client/serveur.
 * Toute duree exposee au joueur est en secondes ; toute duree utilisee par le
 * moteur (timers, scoring) est en millisecondes et suffixee _MS.
 */
// ---------------------------------------------------------------------------
// Joueurs et format de partie
// ---------------------------------------------------------------------------
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 10;
/**
 * Reglages numeriques : des raccourcis proposes dans l'interface, PLUS une
 * plage libre bornee.
 *
 * Les bornes sont la vraie regle : le protocole ne valide QUE l'appartenance a
 * l'intervalle et le caractere entier. Les listes d'options ne sont qu'une
 * commodite d'affichage, elles ne restreignent rien cote serveur.
 */
export const ROUNDS_OPTIONS = [3, 5, 7];
export const ROUNDS_MIN = 1;
export const ROUNDS_MAX = 16;
export const DEFAULT_ROUNDS = 5;
/**
 * En mode « un seul dessine », le reglage ne compte plus des manches mais des
 * TOURS COMPLETS : un tour = un dessin par joueur. Une partie n'y est equitable
 * que si le tour est complet, d'ou ce compte-la plutot qu'un nombre de manches
 * libre.
 *
 * PAS DE VALEUR LIBRE ICI, contrairement a tous les autres reglages, et c'est
 * delibere : le cout d'un tour depend de l'EFFECTIF, ce qui rend le chiffre
 * saisi trompeur. Un joueur a regle trois tours a deux personnes, six joueurs
 * sont arrives, et la partie a dure dix-huit manches sans que rien ne le lui
 * dise. Trois raccourcis, et l'interface affiche le nombre de manches REEL sur
 * chaque bouton (cf. GameSettings) : plus rien a saisir, plus rien a
 * interpreter.
 *
 * CYCLES_MAX vaut donc exactement le dernier raccourci : toute valeur
 * au-dela serait invisible dans le selecteur, faute de case ou l'afficher.
 */
export const CYCLES_OPTIONS = [1, 2, 3];
export const CYCLES_MIN = 1;
export const CYCLES_MAX = 3;
export const DEFAULT_CYCLES = 2;
export const DRAW_TIME_OPTIONS = [30, 60, 90, 120, 150];
export const DRAW_TIME_MIN = 15;
/**
 * Plancher ABSOLU du temps de dessin, tous modes confondus.
 *
 * Le PROTOCOLE valide contre celui-ci et pas contre la plage d'un mode : un
 * message peut changer le mode et la duree a la fois, il ne sait pas encore
 * lequel s'appliquera. C'est la room qui tranche, elle connait le mode vise.
 *
 * Vaut le MINIMUM des minimums de tous les modes : aujourd'hui 15, celui de la
 * plage ordinaire, le collaboratif ne descendant plus sous 20. La valeur a deja
 * bouge une fois (10 quand le collaboratif proposait 10 s) : si un mode
 * descend un jour plus bas, c'est ICI qu'on l'abaisse, sinon le protocole
 * rejette un reglage que le selecteur du mode propose pourtant.
 */
export const ANY_DRAW_TIME_MIN = 15;
export const DRAW_TIME_MAX = 420;
export const DEFAULT_DRAW_TIME = 120;
/**
 * Temps de dessin d'une PARTIE RAPIDE, plus court que le defaut.
 *
 * Une table d'inconnus n'a pas la patience d'un salon entre amis : personne n'y
 * a choisi la duree, et deux minutes a regarder les autres dessiner suffisent a
 * faire fermer l'onglet. Le salon cree a la main, lui, garde
 * DEFAULT_DRAW_TIME : son hote a decide de jouer, et il peut regler.
 */
export const QUICK_PLAY_DRAW_TIME = 90;
/**
 * Delai avant qu'une partie rapide se relance toute seule, une fois qu'au moins
 * un joueur a demande la revanche.
 *
 * Entre amis, on attend le clic de chacun : c'est une table qu'on a montee
 * ensemble. Entre inconnus, attendre le clic de quelqu'un qui a peut-etre deja
 * ferme son onglet, c'est rester bloque sur un ecran de fin. Le compte a rebours
 * est AFFICHE : une relance qui tombe sans prevenir se lit comme un bug.
 */
export const QUICK_PLAY_REPLAY_MS = 15_000;
export const GUESS_TIME_OPTIONS = [20, 30, 45];
export const GUESS_TIME_MIN = 10;
export const GUESS_TIME_MAX = 180;
export const DEFAULT_GUESS_TIME = 30;
/** Un reglage numerique est-il dans sa plage ? Entier, bornes incluses. */
export function isInRange(value, min, max) {
    return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}
/**
 * Mode par defaut : la chasse commune. Tout le salon regarde LE MEME tableau et
 * cherche en meme temps, ce qui donne une partie ou il se passe quelque chose en
 * permanence. Le classique, lui, isole chacun devant son propre tableau.
 */
export const DEFAULT_GAME_MODE = 'hunt';
/**
 * Plafond de salons renvoyes par LIST_ROOMS.
 *
 * Le listing est un instantane a la demande : sans plafond, un pic de salons
 * ouverts ferait grossir la reponse sans limite, et le navigateur rendrait des
 * centaines de cartes que personne ne lit. Le serveur renvoie les plus remplis
 * en premier, et annonce le total pour que l'interface puisse le dire.
 */
export const ROOM_LIST_LIMIT = 30;
/** Salons affiches par page dans /browse. */
export const ROOM_PAGE_SIZE = 8;
// ---------------------------------------------------------------------------
// Durees de phases
// ---------------------------------------------------------------------------
export const COUNTDOWN_TIME = 3;
/**
 * Duree de revelation par tableau. Volontairement courte : passe ~5 s, on a
 * compris ce qu'on regardait et la sequence traine. La consultation libre en
 * fin de phase (REVEAL_BROWSE_TIME) est la pour ceux qui veulent s'attarder.
 */
export const REVEAL_TIME = 5;
/**
 * Rallonge accordee EN FIN de phase de revelation, apres le defile automatique.
 * C'est le seul moment ou les joueurs peuvent consulter librement les retouches
 * des autres dans la pellicule ; sans ce battement, la manche suivante demarre
 * pendant qu'ils regardent encore.
 */
export const REVEAL_BROWSE_TIME = 8;
/** Marge accordee apres la fin du timer de dessin pour collecter les envois. */
export const COLLECT_GRACE = 3;
export const COUNTDOWN_TIME_MS = COUNTDOWN_TIME * 1000;
/**
 * Valeur par defaut du temps de devinette, en ms. Ce n'est PAS la duree
 * effective : celle-ci est un reglage de salon (settings.guessTime) et doit
 * etre lue sur le RoomSnapshot. Cette constante ne sert qu'a initialiser un
 * salon neuf et les ecrans hors partie (sandbox).
 */
export const DEFAULT_GUESS_TIME_MS = DEFAULT_GUESS_TIME * 1000;
export const REVEAL_TIME_MS = REVEAL_TIME * 1000;
export const REVEAL_BROWSE_MS = REVEAL_BROWSE_TIME * 1000;
/**
 * Duree totale de la phase REVEAL pour `itemCount` tableaux : le defile
 * automatique, puis le temps de consultation libre.
 *
 * Serveur et client la calculent avec CETTE fonction : l'index affiche est
 * derive de `endsAt`, la moindre divergence de formule ferait defiler les
 * onglets a des rythmes differents.
 */
export function revealDurationMs(itemCount) {
    return Math.max(itemCount, 1) * REVEAL_TIME_MS + REVEAL_BROWSE_MS;
}
/** Duree du DEFILE seul : un passage, REVEAL_TIME par retouche. */
export function revealParadeMs(itemCount) {
    return Math.max(itemCount, 1) * REVEAL_TIME_MS;
}
/**
 * Tableau montre a l'instant `elapsedMs` du DEFILE (hors consultation libre).
 *
 * Chaque retouche recoit exactement REVEAL_TIME, sans exception. C'est le point
 * important : un index simplement borne laissait la derniere absorber toute la
 * consultation libre et rester affichee REVEAL_TIME + REVEAL_BROWSE_TIME, soit
 * 13 s contre 5 s pour les autres. Au-dela du defile la fonction boucle, mais
 * l'appelant n'est pas cense s'y aventurer : la consultation libre ne se joue
 * plus sur le defile (cf. RevealScreen).
 */
export function revealIndexAt(elapsedMs, itemCount) {
    if (itemCount <= 0)
        return 0;
    if (!Number.isFinite(elapsedMs) || elapsedMs < 0)
        return 0;
    const step = Math.floor(elapsedMs / REVEAL_TIME_MS);
    return ((step % itemCount) + itemCount) % itemCount;
}
export const COLLECT_GRACE_MS = COLLECT_GRACE * 1000;
/**
 * Court moment, au tout debut de la recherche, ou un clic ne compte pas.
 *
 * On sort de la phase de dessin. Le joueur avait le doigt ou la souris en
 * mouvement, le plateau change sous lui, et le clic qui devait prolonger un
 * trait tombe sur le tableau de chasse. Sans ce delai, ce geste-la vaut une
 * erreur, et l'erreur est definitive : elle ferme la manche de son auteur.
 *
 * Il court depuis l'AFFICHAGE du plateau chez ce joueur, pas depuis le
 * changement de phase cote serveur : ce qu'on protege est un temps de
 * reaction humain, il doit donc partir du moment ou l'ecran a change sous
 * ses yeux, reseau compris.
 *
 * SEUL LE CLIENT l'applique, et c'est delibere : le serveur ignorerait un clic
 * que le client, lui, aurait deja compte comme un essai, et le joueur serait
 * verrouille a l'ecran pour une devinette que personne n'a enregistree. C'est
 * la meme famille de filtre que les clics neutres ou ceux d'un joueur
 * verrouille, deja traites cote client. Il n'y a rien a y gagner par la
 * triche : la demi-seconde tombe dans le plateau du bareme, ou tout clic vaut
 * deja le maximum.
 */
export const GUESS_BLIND_MS = 500;
// ---------------------------------------------------------------------------
// Scoring (cf. CLAUDE.md : implemente dans scoring.ts)
// ---------------------------------------------------------------------------
/** Plateau pendant lequel le devineur marque le maximum. */
export const GUESSER_PLATEAU_MS = 3000;
export const GUESSER_MAX_SCORE = 1000;
export const GUESSER_MIN_SCORE = 100;
export const DRAWER_MIN_SCORE = 100;
export const DRAWER_MAX_SCORE = 800;
// ---------------------------------------------------------------------------
// Encodage des dessins
// ---------------------------------------------------------------------------
/** Grille de quantification des points, relative a la zone de dessin. */
export const DRAWING_GRID = 4096;
/** Taille max d'un blob de dessin accepte par le serveur. */
export const MAX_DRAWING_BYTES = 32 * 1024;
/** Profondeur de la pile d'undo (par trait, pas de redo). */
export const UNDO_STACK_SIZE = 30;
// ---------------------------------------------------------------------------
// Reseau et securite
// ---------------------------------------------------------------------------
/** Alphabet des codes de room : A-Z2-9 sans caracteres ambigus (I, O, 0, 1). */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 6;
export const NICKNAME_MIN_LENGTH = 2;
export const NICKNAME_MAX_LENGTH = 16;
/** Rate limit par connexion, hors messages de dessin. */
export const RATE_LIMIT_MESSAGES_PER_SECOND = 10;
/** Taille max d'un message JSON de controle. */
export const MAX_CONTROL_MESSAGE_BYTES = 4 * 1024;
/** Delai de grace pour se reconnecter avec son token de session. */
export const RECONNECT_GRACE_MS = 60_000;
export const SERVER_PORT = 9001;
/**
 * URL publique du JEU, utilisee dans l'image de partage et les intents sociaux.
 *
 * Ce n'est PAS un lien vers une partie ou vers des images hebergees : aucune
 * retouche n'est stockee cote serveur, ni pendant ni apres la partie. L'image de
 * partage est composee dans le navigateur et voyage comme fichier (navigator.share
 * ou telechargement). Il n'y a donc rien a conserver, et rien qui grossisse avec
 * le nombre de parties jouees.
 *
 * Domaine de production. VITE_SHARE_URL le remplace au build (cf.
 * client/src/config.ts) ; cette valeur est le repli, et elle doit rester
 * d'accord avec les balises Open Graph de index.html, qui ne peuvent pas etre
 * substituees au build (les robots n'executent pas le bundle).
 */
export const SHARE_URL = 'https://retusch.io';
// ---------------------------------------------------------------------------
// Anti-flood du chat et des reactions
// ---------------------------------------------------------------------------
/**
 * Le rate limit general (10 msg/s) laisse passer un flot largement suffisant
 * pour rendre une partie insupportable : 10 messages par seconde noient le
 * panneau et couvrent l'ecran d'emoji. Ces deux canaux, qui s'ADRESSENT aux
 * autres joueurs, ont donc leur propre budget, bien plus serre.
 *
 * Seau a jetons : un envoi consomme un jeton, les jetons se rechargent a
 * `*_REFILL_MS`. Une rafale courte passe (on tape vite quand on reagit), un
 * flux continu non.
 */
export const CHAT_BURST = 4;
export const CHAT_REFILL_MS = 1500;
/** Deux messages identiques d'affilee : le second est ignore. */
export const CHAT_REPEAT_WINDOW_MS = 10_000;
export const REACTION_BURST = 5;
export const REACTION_REFILL_MS = 800;
/** Nombre max de tableaux dans l'image de partage. */
export const SHARE_MAX_ARTWORKS = 9;
// ---------------------------------------------------------------------------
// Bot de la partie rapide
// ---------------------------------------------------------------------------
/**
 * Le bot rend son dessin entre ces deux fractions de la phase de dessin, sans
 * jamais depasser BOT_DRAW_MAX_MS. Il ne doit pas faire attendre l'humain : la
 * manche avance des que tout le monde a valide, et un bot lent transformerait
 * la partie en salle d'attente.
 */
export const BOT_DRAW_MIN_FRACTION = 0.1;
export const BOT_DRAW_MAX_FRACTION = 0.3;
export const BOT_DRAW_MAX_MS = 10_000;
/**
 * Le bot trouve la retouche adverse entre la moitie et la toute fin de la phase
 * de recherche. La borne haute laisse une marge sous 100 % : un clic pose
 * exactement a l'expiration arriverait apres la fermeture de la phase.
 */
export const BOT_FIND_MIN_FRACTION = 0.5;
export const BOT_FIND_MAX_FRACTION = 0.98;
// ---------------------------------------------------------------------------
// Rendu et interaction
// ---------------------------------------------------------------------------
/** Part de la largeur ecran occupee par la zone en auto-zoom (phase dessin). */
export const DRAW_ZOOM_RATIO = 0.80;
/** Deplacement (px) au-dela duquel un pointeur est un pan, pas un clic. */
export const CLICK_MOVE_THRESHOLD_PX = 8;
/**
 * Echelle des tailles de trait. Le champ `size` du format binaire est un uint8,
 * donc une taille s'exprime en 256e de la largeur de la zone : une size de 10
 * vaut 10/256 = 3,9 % de la largeur de la zone, quelle que soit la resolution
 * de rendu. C'est ce qui permet au meme dessin de s'afficher a l'identique sur
 * un telephone et sur un ecran de bureau.
 */
export const ZONE_UNITS = 256;
/**
 * Les 3 tailles de pinceau, en unites zone (cf. ZONE_UNITS), soit 2,3 %, 5,5 %
 * et 10,9 % de la largeur de la zone.
 *
 * Rehaussees depuis [4, 10, 22] : sur une zone qui fait 10 % du tableau, le
 * petit pinceau demandait un nombre de passages decourageant pour couvrir quoi
 * que ce soit, et le gros restait juste en dessous de ce qu'on attend d'un
 * gros. Le curseur de taille exacte, lui, garde toute sa plage.
 */
export const BRUSH_SIZES = [6, 14, 28];
export const DEFAULT_BRUSH_SIZE_INDEX = 1;
/**
 * Preselection allumee pour une taille exacte : la plus grande dont la valeur
 * est atteinte.
 *
 * Le curseur de taille et les trois boutons designent LA MEME chose. Tant que
 * les boutons ne suivaient pas le curseur, on pouvait pousser la taille bien
 * au-dela du gros pinceau en gardant le petit allume : les deux commandes se
 * contredisaient a l'ecran, et on finissait par ne plus croire ni l'une ni
 * l'autre.
 */
export function brushPresetIndex(size) {
    let index = 0;
    for (let i = 0; i < BRUSH_SIZES.length; i += 1) {
        if (size >= BRUSH_SIZES[i])
            index = i;
    }
    return index;
}
// ---------------------------------------------------------------------------
// Couleurs
// ---------------------------------------------------------------------------
/**
 * Palette par defaut, en attendant l'extraction offline des couleurs de chaque
 * tableau. 10 couleurs vives.
 */
export const DEFAULT_PALETTE = [
    '#e5342f',
    '#f2762e',
    '#f5b93b',
    '#f2e14c',
    '#5cc45c',
    '#1f9e78',
    '#2f8fd9',
    '#2c3f8f',
    '#8f4bc4',
    '#e0559b',
];
/** Couleurs joueurs (10, desaturees) : cf. design system. */
export const PLAYER_COLORS = [
    '#c76b6b',
    '#c79b6b',
    '#b8c76b',
    '#6bc78a',
    '#6bbcc7',
    '#6b8ac7',
    '#9b6bc7',
    '#c76bb0',
    '#8a8f99',
    '#c7c46b',
];
// ---------------------------------------------------------------------------
// Partage vers Discord
// ---------------------------------------------------------------------------
/**
 * Poids maximal du GIF accepte par la route de partage.
 *
 * Marge sous la limite de piece jointe d'un webhook Discord (8 Mo pour un
 * serveur sans boost) : au-dela, Discord refuse et le relais aurait porte le
 * fichier pour rien. Le CLIENT verifie avant d'envoyer et le serveur revalide,
 * pour la meme raison que partout ailleurs : le client n'est pas une autorite.
 */
export const MAX_SHARE_GIF_BYTES = 7 * 1024 * 1024;
/**
 * Partages autorises pour UN MEME tableau de la galerie, par joueur et par
 * partie. UN SEUL : la meme image postee deux fois n'apporte rien a personne
 * et encombre un canal que nous ne moderons pas.
 *
 * Ce plafond n'est tenable a 1 que parce que la place n'est CONSOMMEE QUE SI
 * l'envoi part reellement (cf. `Room.releaseShare`). Sans ce rattrapage, une
 * file pleine brulerait l'unique droit du joueur pour un partage qui n'a
 * jamais eu lieu, et l'image deviendrait definitivement impartageable.
 */
export const SHARES_PER_ITEM = 1;
/** Partages autorises pour une partie entiere, tous joueurs confondus. */
export const SHARES_PER_ROOM = 5;
//# sourceMappingURL=constants.js.map